import EventEmitter from 'node:events';
import React, { Component } from 'react';
import stripAnsi from 'strip-ansi';
import type { FC, ReactElement, ReactNode } from 'react';
import type { RenderOptions, Instance as InkInstance } from 'ink';
import { render as inkRender, Text } from 'ink';
import { getTestOverrides } from '@wix/cli-test-overrides';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { useExit } from './hooks/use-exit.js';
import type { ExitFn } from './hooks/use-exit.js';

class ErrorBoundary extends Component<
  { children: ReactNode; onExit: ExitFn },
  { hasError: boolean }
> {
  override state = { hasError: false };
  static getDerivedStateFromError(_error: unknown) {
    return { hasError: true };
  }

  override componentDidCatch(e: unknown) {
    const error = e instanceof Error ? e : new Error(JSON.stringify(e));

    if (
      error.message.includes(
        'Raw mode is not supported on the current process.stdin, which Ink uses as input stream by default'
      )
    ) {
      this.props.onExit(
        new CliError({
          code: CliErrorCode.NonInteractiveTerminal(),
          cause: error,
        })
      );
    } else {
      this.props.onExit(error);
    }
  }

  override render() {
    return this.state.hasError ? null : this.props.children;
  }
}

const ErrorBoundaryWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  const exit = useExit();

  return <ErrorBoundary onExit={exit}>{children}</ErrorBoundary>;
};

const FrameWrapper: FC<{ children: ReactNode; wrap: boolean }> = ({
  children,
  wrap,
}) => {
  return (
    <>
      {wrap && <Text>----RENDER-FRAME-START----</Text>}
      {children}
      {wrap && <Text>----RENDER-FRAME-END----</Text>}
    </>
  );
};

export async function render(tree: ReactElement): Promise<void> {
  const debug = !!getTestOverrides().inkDebug;
  const { waitUntilExit, unmount } = inkRender(
    <FrameWrapper wrap={debug}>
      <ErrorBoundaryWrapper>{tree}</ErrorBoundaryWrapper>
    </FrameWrapper>,
    { exitOnCtrlC: true, debug }
  );

  // If we don't unmount, the app render promise (which returns from
  // `waitUntilExit`) will never resolve, and any code after rendering will not
  // be executed (most commonly sending a "command success" BI event). The
  // process will still exit because node doesn't wait for pending promises, so
  // the end result is the process will exit without running any code that
  // happens after the render call.
  process.once('beforeExit', () => {
    unmount();
  });

  /**
   * ink handles ctrl+c and unmounts the app only if it listens to input (by a
   * component using `useInput`). Otherwise it doesn't handle it, meaning it's
   * being handled by node, which exits the process. When that happens, we don't
   * get to any code that waits for the rendering to finish (usually command end
   * BI event). This is an unpredictable behavior, because it depends on whether
   * some component listens to input or not. Therefore, we listen on SIGINT
   * (which is fired when ctrl+c is pressed) and unmount
   */
  process.once('SIGINT', () => {
    unmount();
  });

  await waitUntilExit();
}

function renderSyncInternal(
  tree: ReactElement,
  renderOptions?: RenderOptions
): void {
  const debug = !!getTestOverrides().inkDebug;
  const { unmount } = inkRender(
    <FrameWrapper wrap={debug}>{tree}</FrameWrapper>,
    {
      debug,
      ...renderOptions,
    }
  );

  unmount();
}

export function renderSync(tree: ReactElement): void {
  renderSyncInternal(tree);
}

export function renderErrorSync(tree: ReactElement): void {
  renderSyncInternal(tree, {
    stdout: process.stderr,
  });
}

class FakeStream extends EventEmitter {
  readonly columns = 500;
  private _lastFrame?: string;
  private stripAnsi: boolean;

  constructor({ stripAnsi }: { stripAnsi: boolean }) {
    super();
    this.stripAnsi = stripAnsi;
  }

  write(frame: string) {
    this._lastFrame = this.stripAnsi ? stripAnsi(frame) : frame;
  }

  lastFrame = () => this._lastFrame ?? '';
}

export function createRendererToString({ stripAnsi }: { stripAnsi: boolean }) {
  let instance: InkInstance | undefined;
  const stdout = new FakeStream({ stripAnsi });

  return {
    waitUntilExit: async () => {
      return instance?.waitUntilExit();
    },
    render: (tree: ReactElement) => {
      const wrappedTree = <ErrorBoundaryWrapper>{tree}</ErrorBoundaryWrapper>;

      if (instance) {
        instance.rerender(wrappedTree);
      } else {
        instance = inkRender(wrappedTree, {
          stdout: stdout as never,
          debug: true,
          exitOnCtrlC: false,
          patchConsole: false,
        });
      }

      return stdout.lastFrame();
    },
    unmount: () => {
      instance?.unmount();
      instance?.cleanup();
    },
  };
}
